import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useAuthSession, type StoreMemberRole, type SupabaseClient } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';

export interface NewsletterSubscriberPermissions {
  readonly role: StoreMemberRole | null;
  readonly canManageNewsletterSubscribers: boolean;
}

export const newsletterSubscriberPermissionKeys = {
  role: (storeId: string | null, userId: string | null) =>
    ['newsletter-subscribers', 'permissions', storeId, userId] as const,
};

/** Navigation convenience only; the Admin RPCs remain authoritative. */
export function useNewsletterSubscriberPermissions(
  storeId: string | null,
  client: SupabaseClient = supabase,
): UseQueryResult<NewsletterSubscriberPermissions> {
  const session = useAuthSession(client);
  const userId = session.user?.id ?? null;
  return useQuery({
    queryKey: newsletterSubscriberPermissionKeys.role(storeId, userId),
    enabled: Boolean(storeId && userId && !session.isLoading),
    staleTime: 0,
    queryFn: async (): Promise<NewsletterSubscriberPermissions> => {
      if (!storeId || !userId) throw new Error('missing newsletter permission scope');
      const { data, error } = await client
        .from('store_members')
        .select('role')
        .eq('store_id', storeId)
        .eq('user_id', userId)
        .is('deleted_at', null)
        .maybeSingle<{ role: StoreMemberRole }>();
      if (error) throw error;
      const role = data?.role ?? null;
      return { role, canManageNewsletterSubscribers: role === 'owner' || role === 'admin' };
    },
  });
}
